import React, { MouseEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "../common/SelectInput";
import SchemaForm from "../common/SchemaForm";
import { NewIntegration, DestinationType, Organization } from "../common/ObjectTypes";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { checkSchema, isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { isEarthRangerDestinationType } from "./earthRangerUrl";
import EarthRangerSiteInput from "./EarthRangerSiteInput";
import { getButtonText } from "../common/TestActionFunctions";
import { Tooltip } from "../common/Tooltip";
import { useHelp, HelpCard } from "../../context/HelpContext";

type DestinationModalProps = {
    currentMemberIsAdmin: boolean,
    errors: any,
    rsjfErrors: any,
    onToggleModal: MouseEventHandler,
    destination: NewIntegration,
    destinationType: DestinationType,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    orgSearchActive?: boolean,
    destinationTypes: DestinationType[],
    onChange: Function,
    onChangeOrg: Function,
    onChangeType: Function,
    onChangeJSONForm: Function,
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    onGoToDetails: (destination_id: string) => void
}

const DestinationModal = ({
    currentMemberIsAdmin,
    errors,
    rsjfErrors,
    onToggleModal,
    destination,
    destinationType,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    orgSearchActive = false,
    destinationTypes,
    onChange,
    onChangeOrg,
    onChangeType,
    onChangeJSONForm,
    onSave,
    onGoToDetails
}: DestinationModalProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [showAdvanced, setShowAdvanced] = useState(false);
    const shouldHideUrlField = isHiddenUrlDestinationType(destinationType.name);
    const isEarthRanger = isEarthRangerDestinationType(destinationType.name);

    // Track if a type has been selected
    const typeSelected = destinationType && destinationType.id && destinationType.id !== '0';

    useEffect(() => {
        if (!typeSelected) {
            setHelpContent(null);
            return;
        }
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Destination in Gundi'),
                description: t('Find step-by-step instructions for connecting your destination.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/destinations' },
            },
            ...(destinationType.help_center_url ? [{
                title: t('{{technology}} Destination Set Up', { technology: destinationType.name }),
                description: t('Configuration guide specific to {{technology}} destinations.', { technology: destinationType.name }),
                button: { label: t('Learn More'), url: destinationType.help_center_url },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [typeSelected, destinationType, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    return (
        <div className="z-20 flex min-h-screen bg-very-light-gray transition-colors duration-300">
            <div className="flex flex-col w-full min-h-screen my-4">
                <div className="pb-6">
                    <div>
                        <div className="max-w-list-table mx-auto">
                            <h1 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-8 transition-colors duration-300">{t("New Destination")}</h1>
                        </div>
                        <div className="flex flex-col justify-between h-screen">
                            <div className="flex flex-row justify-center max-w-list-table mx-auto w-full">
                                <div className="w-full pb-8">
                                    {/* STEP 1: DESTINATION TYPE */}
                                    <div className="px-4 sm:px-6">
                                        <div className="flex flex-col min-w-[400px]">
                                            <label>{t("Type")}</label>
                                            <SelectInput
                                                options={destinationTypes.map((type: DestinationType) => ({
                                                    label: type.name,
                                                    name: type.name,
                                                    description: type.description,
                                                    id: type.id,
                                                    value: type.id,
                                                    actions: type.actions,
                                                    help_center_url: type.help_center_url
                                                }))}
                                                className={"basic-single text-black"}
                                                defaultValue={{
                                                    label: "Select",
                                                    value: 0
                                                }}
                                                isDisabled={false}
                                                isLoading={false}
                                                isClearable={false}
                                                isSearchable={true}
                                                isMulti={false}
                                                name={t("Type")}
                                                onChange={(e: any) => onChangeType(e)}
                                            />
                                        </div>
                                    </div>

                                    {/* STEP 2: NAME, CONFIGURATION, ENABLED */}
                                    {typeSelected && (
                                        <div className="h-full w-full mb-8 flex flex-col justify-between">
                                            <div className="px-2 mx-3">
                                                {/* Configuration section */}
                                                <div className="mt-8">
                                                    <div className="text-lg font-semibold text-secondary-gray transition-all duration-300 mb-4">
                                                        {t("Configuration")}
                                                    </div>
                                                    <div className="rounded border border-gray-lines bg-white p-5 transition-colors duration-300">
                                                        {/* Name */}
                                                        <div className="mb-[8px] border-b border-divider-lines pt-1 pb-[8px] transition-all duration-300 w-full">
                                                            <div className="flex flex-row items-start justify-between w-full gap-6">
                                                                <div className="w-1/2 flex flex-col">
                                                                    <label className="mr-1">{t("Name")} *</label>
                                                                    <span className="text-sm text-secondary-gray mt-1">{t("A descriptive name for this destination.")}</span>
                                                                </div>
                                                                <div className="w-1/2">
                                                                    <input
                                                                        id="name"
                                                                        placeholder=""
                                                                        className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                                                        value={destination.name || ""}
                                                                        onChange={(e) => onChange(e)}>
                                                                    </input>
                                                                    {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* Workspace */}
                                                        <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                                                            <div className="flex flex-row items-start justify-between w-full gap-6">
                                                                <div className="w-1/2 flex flex-col">
                                                                    <label className="mr-1">{t("Workspace")} *</label>
                                                                    <span className="text-sm text-secondary-gray mt-1">{t("The workspace this destination belongs to.")}</span>
                                                                </div>
                                                                <div className="w-1/2">
                                                                    <SelectInput
                                                                        options={organizations.map((org: Organization) => {
                                                                            return {
                                                                                label: org.name,
                                                                                value: org.id
                                                                            }
                                                                        })}
                                                                        className={"basic-single text-black"}
                                                                        autoSelectSingleOption={!orgSearchActive}
                                                                        isDisabled={false}
                                                                        isLoading={false}
                                                                        isClearable={false}
                                                                        isSearchable={true}
                                                                        isMulti={false}
                                                                        name={t("Workspace")}
                                                                        onChange={(e: any) => onChangeOrg(e)}
                                                                        onMenuScrollToBottom={loadMoreOrgs}
                                                                        handleInputChange={orgInputChange}
                                                                    />
                                                                    {errors.owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* URL */}
                                                        {!shouldHideUrlField && (
                                                            <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                                                                <div className="flex flex-row items-start justify-between w-full gap-6">
                                                                    <div className="w-1/2 flex flex-col">
                                                                        <label className="mr-1">{isEarthRanger ? t("Site URL") : t("URL")} *</label>
                                                                        <span className="text-sm text-secondary-gray mt-1">{isEarthRanger ? t("The name of the EarthRanger site (e.g. my-site).") : t("The base URL of the destination.")}</span>
                                                                    </div>
                                                                    <div className="w-1/2">
                                                                        {isEarthRanger ? (
                                                                            <EarthRangerSiteInput
                                                                                value={destination.base_url || ""}
                                                                                onChange={(e) => onChange(e)}
                                                                                error={errors.base_url}
                                                                            />
                                                                        ) : (
                                                                            <>
                                                                                <input
                                                                                    id="base_url"
                                                                                    placeholder=""
                                                                                    className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                                                                    value={destination.base_url || ""}
                                                                                    onChange={(e) => onChange(e)}>
                                                                                </input>
                                                                                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* configuration */}
                                                <div className="pt-14">
                                                    {/* <label>Configurations</label> */}
                                                    {destinationType.actions.filter((item: any) => item.type !== "pull").map((item: any) => {
                                                        const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                                        const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                                                        const hasSchema = checkSchema(item);
                                                        const isVisible = isActionVisible(item);
                                                        return (
                                                            <div key={item.id} className={isVisible ? "pb-14" : "hidden"}>
                                                                {isVisible && (
                                                                    <>
                                                                        <div className="flex justify-between items-start w-full mb-4">
                                                                            <div className="text-2xl font-normal text-secondary-gray transition-all duration-300">{formTitle}</div>
                                                                            {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true && (
                                                                                <div className="flex flex-row items-center">
                                                                                    <button disabled className="opacity-100 bg-light-gray-button text-secondary-gray font-semibold rounded py-2 px-4 ml-1 mr-3 shadow">
                                                                                        {getButtonText(item.value, t)}
                                                                                    </button>
                                                                                    <Tooltip description={t("Save your destination first to enable this feature.")} position="right" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {item.schema?.properties && Object.keys(item.schema.properties).length > 0 && <div className="rounded border border-gray-lines bg-white p-5 mb-8 transition-colors duration-300">
                                                                            <SchemaForm
                                                                                userIsAdmin={currentMemberIsAdmin}
                                                                                formTitle={''}
                                                                                schema={item.schema}
                                                                                ui_schema={uiSchema}
                                                                                onChange={(e: any) => onChangeJSONForm(e, item.id)}
                                                                                isEditable={true}
                                                                                errors={rsjfErrors[item.id]}
                                                                            />
                                                                        </div>}
                                                                    </>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                {/* advanced configurations */}
                                                {shouldHideUrlField && <div className="pb-14">
                                                    <div className="mt-6 text-lg font-semibold text-secondary-gray cursor-pointer transition-colors duration-300" onClick={() => setShowAdvanced(!showAdvanced)}>
                                                        {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                                                    </div>
                                                    {showAdvanced &&
                                                        <div className="ml-8 flex flex-col">
                                                            <label className="text-sm text-secondary-gray transition-colors duration-300">{t("URL")}</label>
                                                            <input
                                                                id="base_url"
                                                                placeholder=""
                                                                className="mt-2 p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                                                value={destination.base_url ? destination.base_url : ""}
                                                                onChange={(e) => onChange(e)}>
                                                            </input>
                                                            {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                                        </div>}
                                                </div>}
                                            </div>

                                        </div>

                                    )}
                                </div>
                            </div>
                            {/* submit and cancel button */}
                            <div className="sticky bottom-0 left-0 bg-white/50 backdrop-blur-sm flex flex-row py-3 px-4 sm:px-6 w-full transition-colors duration-300 z-50">
                                <button className={typeSelected ? "bg-dark-green text-white font-bold rounded px-7 py-2" : "bg-light-green hover:bg-light-green-hover text-neutral-gray-600 font-bold rounded px-7 py-2 transition-colors duration-300"}
                                    type="submit"
                                    disabled={!typeSelected}
                                    aria-disabled={!typeSelected}
                                    onClick={(e: any) => onSave(e, "save")}>
                                    {t("Save")}
                                </button>
                                <button className={typeSelected ? "bg-light-green hover:bg-light-green-hover text-dark-green font-bold rounded px-7 py-2 mx-3 transition-colors duration-300" : "bg-neutral-gray-50 text-neutral-gray-600 font-bold rounded px-7 py-2 mx-3"}
                                    type="submit"
                                    disabled={!typeSelected}
                                    aria-disabled={!typeSelected}
                                    onClick={(e: any) => onSave(e, "saveAndClose")}>
                                    {t("Save and Close")}
                                </button>
                                <button className="font-bold text-medium-gray rounded px-7 mx-2 transition-colors duration-300"
                                    type="button"
                                    onClick={onToggleModal}>
                                    {t("Cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DestinationModal;